import React, { useEffect, useRef, useState } from "react";
import { Skeleton, Button } from "@mui/material";
import {
  Lightbulb as LightbulbIcon,
  Newspaper as NewspaperIcon,
  FormatQuote as QuoteIcon,
  ContentCopyOutlined as ContentCopyOutlinedIcon,
  Event as EventIcon,
} from "@mui/icons-material";
import Markdown from "react-markdown";
import foundryMarkdown from "../../../lib/FoundryMarkdown";
import PillsGroup from "../PillsGroup/PillsGroup";
import SourcePill from "../SourcePill/SourcePill";
import { Link } from "react-router-dom";
import { markdownComponents } from "../MarkdownComponents/MarkdownComponents";
import styles from "./AnalysisContent.module.css";
import { Osdk } from "@osdk/client";
import { Analysis } from "@unruly-osdk/sdk";
import { useServices } from "../../services/Services";
import {
  formatDate,
  getDomainFromUrl,
  cleanHtmlForCopy,
  getInternalSourceLabel,
  isInternalEvent,
} from "../../helper/helper";
import { format } from "date-fns";
import { PillsVariant } from "../../common-types/Pill";
import { usePageTitle } from "../../context/usePageTitle";

interface AnalysisSection {
  heading: string;
  content: Array<{
    text: string;
    type: "paragraph" | "insight";
    sources: string[];
  }>;
}

interface AnalysisContentProps {
  analysis: Osdk.Instance<Analysis> | null;
  isLoading?: boolean;
  userId?: string;
  onOpenAnalysis?: (analysis: Osdk.Instance<Analysis>, eventId: string) => void;
  showShareDialog?: (eventId: string) => void;
}

const AnalysisContent: React.FC<AnalysisContentProps> = ({
  analysis,
  isLoading = false,
  userId,
  onOpenAnalysis,
  showShareDialog,
}) => {
  const services = useServices();
  const { setPageTitle } = usePageTitle();
  const contentRef = useRef<HTMLDivElement>(null);
  const [copySuccess, setCopySuccess] = useState(false);

  // Fetch events associated with this analysis
  const { data: analysedEvents } =
    services.Event.useGetEventsAnalysedInAnalysis(analysis?.id || "");
  const { data: referenceEvents } =
    services.Event.useGetEventsReferencedInAnalysis(analysis?.id || "");

  // Set page title when analysis and events are loaded
  useEffect(() => {
    if (analysis) {
      const createdAtFormatted = analysis.createdAt
        ? format(new Date(analysis.createdAt), "EEE, MMM dd, yyyy")
        : "Unknown Date";

      const fallbackTitle =
        analysis.type === "event deep dive"
          ? "Event Deep-Dive"
          : analysis.type === "topic deep dive"
            ? "Topic Deep-Dive"
            : "Analysis";
      if (analysis.type === "event deep dive") {
        setPageTitle(
          analysis.title ?? "Untitled Event Deep-Dive",
          `Event Deep-Dive · ${createdAtFormatted}`,
        );
      } else if (analysis.type === "topic deep dive") {
        setPageTitle(
          analysis.title ?? "Untitled Topic Deep-Dive",
          `Topic Deep-Dive · ${createdAtFormatted}`,
        );
      } else {
        setPageTitle(fallbackTitle, `Analysis · ${createdAtFormatted}`);
      }
    }

    // Cleanup on unmount
    return () => {
      setPageTitle("", "");
    };
  }, [analysis, analysedEvents, setPageTitle]);

  const copyAnalysisToClipboard = async () => {
    try {
      if (!contentRef.current) {
        console.error("Analysis content element not found");
        return;
      }

      // Remove buttons and SVG icons from the content using DOM mutation
      const cleanedHtml = cleanHtmlForCopy(contentRef.current);
      // Create a ClipboardItem with both HTML and plain text formats
      const clipboardItem = new ClipboardItem({
        "text/html": new Blob([cleanedHtml], { type: "text/html" }),
        "text/plain": new Blob([contentRef.current.textContent || ""], {
          type: "text/plain",
        }),
      });

      // Write both formats to clipboard
      await navigator.clipboard.write([clipboardItem]);

      // Show success message briefly
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error("Failed to copy analysis:", err);
    }
  };

  if (!analysis) return null;

  // Create a map of event ID to event data for quick lookup
  const eventsMap = new Map();
  const allEvents = [...(analysedEvents || []), ...(referenceEvents || [])];
  if (allEvents.length > 0) {
    allEvents.forEach((event) => {
      eventsMap.set(event.id, event);
    });
  }

  const parseSections = (sections: string[]): AnalysisSection[] => {
    return sections.map((sectionStr) => {
      try {
        return JSON.parse(sectionStr);
      } catch (error) {
        console.error("Error parsing section:", error);
        return { heading: "Error", content: [] };
      }
    });
  };

  // Create a map of source URL to source number for consistent numbering
  // Numbers are assigned based on when sources first appear in the analysis content
  const sourceNumberMap = new Map();
  let sourceCounter = 1;

  if (analysis?.sections) {
    const sections = parseSections(analysis.sections);
    sections.forEach((section) => {
      section.content.forEach((item) => {
        if (item.sources) {
          item.sources.forEach((eventId: string) => {
            const event = eventsMap.get(eventId);
            const isInternal = isInternalEvent(event?.sourceType);
            const sourceKey = isInternal ? event?.id : event?.newsSourceUrl;
            if (sourceKey && !sourceNumberMap.has(sourceKey)) {
              sourceNumberMap.set(sourceKey, sourceCounter++);
            }
          });
        }
      });
    });
  }

  const sections = parseSections(analysis.sections || []);
  const analysisTypeDisplay =
    analysis.type === "event deep dive"
      ? "Event Deep-Dive"
      : analysis.type === "topic deep dive"
        ? "Topic Deep-Dive"
        : "Analysis";
  const formattedDateDisplay = analysis.createdAt
    ? format(new Date(analysis.createdAt), "EEE, MMM dd, yyyy")
    : "Unknown Date";
  return (
    <div className={styles.wrapper}>
      <section className={styles.container} ref={contentRef}>
        <div className={styles.header}>
          <div className={styles.titleSection}>
            <h1 className={styles.title}>{analysis.title}</h1>
            <p className={styles.subtitle}>
              {analysisTypeDisplay} · {formattedDateDisplay}
            </p>
          </div>
          <div className={styles.actions}>
            <Button
              startIcon={<ContentCopyOutlinedIcon />}
              onClick={copyAnalysisToClipboard}
              sx={{ textTransform: "none" }}
            >
              {copySuccess ? "Copied!" : "Copy Content"}
            </Button>
          </div>
        </div>
        <div className={styles.content}>
          {isLoading ? (
            <div className={styles.loadingSkeleton}>
              <Skeleton
                variant="rectangular"
                height={100}
                className={styles.skeletonEventHeader}
              />
              <Skeleton
                variant="text"
                height={40}
                className={styles.skeletonTitle}
              />
              <Skeleton
                variant="text"
                height={20}
                className={styles.skeletonText}
              />
              <Skeleton
                variant="text"
                height={20}
                className={styles.skeletonText}
              />
              <Skeleton
                variant="text"
                height={20}
                className={styles.skeletonText}
              />
              <Skeleton
                variant="text"
                height={40}
                className={styles.skeletonTitle}
              />
              <Skeleton
                variant="text"
                height={20}
                className={styles.skeletonText}
              />
              <Skeleton
                variant="text"
                height={20}
                className={styles.skeletonText}
              />
              <Skeleton
                variant="text"
                height={20}
                className={styles.skeletonText}
              />
              <Skeleton
                variant="text"
                height={40}
                className={styles.skeletonTitle}
              />
              <Skeleton
                variant="text"
                height={20}
                className={styles.skeletonText}
              />
              <Skeleton
                variant="text"
                height={20}
                className={styles.skeletonText}
              />
              <Skeleton
                variant="text"
                height={20}
                className={styles.skeletonText}
              />
              <Skeleton
                variant="text"
                height={20}
                className={styles.skeletonText}
              />
            </div>
          ) : (
            <>
              {analysis.type === "topic deep dive" && (
                <blockquote className={styles.topicHeader}>
                  <QuoteIcon
                    className={styles.quoteIcon}
                    style={{ width: "24px", height: "24px" }}
                  />
                  <div className={styles.topicInput}>
                    {analysis.subject
                      ?.replace(/^Deep dive on topic:\s*/i, "")
                      .trim()}
                  </div>
                </blockquote>
              )}
              {analysedEvents &&
                analysedEvents.length > 0 &&
                analysedEvents.map((event) => (
                  <div className={styles.eventHeader} key={event.id}>
                    <div className={styles.eventInfo}>
                      <h3 className={styles.eventTitle}>
                        <Link
                          to={`/event/${event.id}`}
                          className={styles.eventTitleLink}
                        >
                          {event.name || "Untitled Event"}
                        </Link>
                      </h3>
                      <PillsGroup
                        pills={[
                          {
                            variant: PillsVariant.default,
                            label: "Date",
                            value: event.startDate
                              ? formatDate(event.startDate)
                              : "Unknown Date",
                            icon: EventIcon,
                          },
                          {
                            variant: PillsVariant.default,
                            label: "Source",
                            value: isInternalEvent(event.sourceType)
                              ? getInternalSourceLabel(event.sourceType)
                              : event.newsSourceUrl
                                ? getDomainFromUrl(event.newsSourceUrl) ||
                                  "Unknown Source"
                                : "Unknown Source",
                            icon: (props) => (
                              <NewspaperIcon
                                {...props}
                                style={{ width: "16px", height: "16px" }}
                              />
                            ),
                            href: isInternalEvent(event.sourceType)
                              ? undefined
                              : event.newsSourceUrl || undefined,
                          },
                        ]}
                      />
                    </div>
                  </div>
                ))}

              <div className={styles.summary}>
                <h4 className={styles.summaryTitle}>Summary</h4>
                <div className={styles.summaryContent}>
                  <Markdown
                    remarkPlugins={[foundryMarkdown]}
                    components={markdownComponents}
                  >
                    {analysis.summary || "No summary available"}
                  </Markdown>
                </div>
              </div>

              <div className={styles.analysis}>
                {sections.map((section, index) => {
                  return (
                    <div key={index} className={styles.section}>
                      <h4 className={styles.sectionHeading}>
                        {section.heading}
                      </h4>
                      {section.content.map((item, itemIndex) => {
                        const itemEventIds = item.sources || [];
                        const itemEvents = itemEventIds
                          .map((eventId: string) => eventsMap.get(eventId))
                          .filter(Boolean);

                        return (
                          <div
                            key={itemIndex}
                            className={`${styles.contentItem} ${
                              item.type === "insight"
                                ? styles.insight
                                : styles.paragraph
                            }`}
                          >
                            {item.type === "insight" && (
                              <LightbulbIcon
                                className={styles.insightIcon}
                                style={{ width: "16px", height: "16px" }}
                              />
                            )}
                            <div className={styles.contentWithEvents}>
                              <div className={styles.markdownContent}>
                                <Markdown
                                  remarkPlugins={[foundryMarkdown]}
                                  components={{
                                    p({ children }) {
                                      return (
                                        <p>
                                          {children}
                                          {itemEvents.length > 0 && (
                                            <span
                                              className={styles.inlineEvents}
                                            >
                                              {itemEvents.map((event) => {
                                                const isInternal =
                                                  isInternalEvent(
                                                    event.sourceType,
                                                  );
                                                const sourceKey = isInternal
                                                  ? event.id
                                                  : event.newsSourceUrl;
                                                const sourceLabel = isInternal
                                                  ? getInternalSourceLabel(
                                                      event.sourceType,
                                                    )
                                                  : getDomainFromUrl(
                                                      event.newsSourceUrl || "",
                                                    ) || "Unknown Source";
                                                return (
                                                  <React.Fragment
                                                    key={event.id}
                                                  >
                                                    {" "}
                                                    <SourcePill
                                                      eventId={event.id}
                                                      eventName={
                                                        event.name ?? undefined
                                                      }
                                                      eventSummary={
                                                        event.eventSummary ??
                                                        undefined
                                                      }
                                                      eventDate={
                                                        event.startDate
                                                          ? formatDate(
                                                              event.startDate,
                                                            )
                                                          : undefined
                                                      }
                                                      sourceUrl={
                                                        event.newsSourceUrl ??
                                                        undefined
                                                      }
                                                      sourceLabel={sourceLabel}
                                                      sourceName={
                                                        event.source ??
                                                        undefined
                                                      }
                                                      sourceNumber={
                                                        sourceNumberMap.get(
                                                          sourceKey,
                                                        ) ?? undefined
                                                      }
                                                      userId={userId}
                                                      onOpenAnalysis={
                                                        onOpenAnalysis
                                                      }
                                                      showShareDialog={
                                                        showShareDialog ??
                                                        (() => {})
                                                      }
                                                    />
                                                  </React.Fragment>
                                                );
                                              })}
                                            </span>
                                          )}
                                        </p>
                                      );
                                    },
                                    ...markdownComponents,
                                  }}
                                >
                                  {item.text}
                                </Markdown>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </section>
    </div>
  );
};

export default AnalysisContent;
